import React, { useCallback, useEffect, useMemo } from 'react'
import RpmFieldChart from './RpmFieldChart'
import { getFieldRegistration, getFieldValidationAttributes } from '../../utils/formValidation'
import { RPM_FIELD_LETTERS, generateRpmFieldName } from '../../constants/rpmFieldConstants'
import { compareVersions } from '../../utils/versionUtils'

function AlternatorSection({ register, errors, watch, onBlur, getFieldError, setValue, deviceInfo }) {
  const formData = watch()

  // Clear checkboxes when they become hidden
  useEffect(() => {
    // Clear alternator 2 top-level checkboxes when switching to single alternator
    // Let the nested dependencies handle rpmReduceField2 → boostMode2 cascade
    if (formData.numAlternatorsInstalled === 'numAlternatorsInstalled1') {
      setValue('statorInstalled2', false)
      setValue('altShuntInstalled2', false)
    }

    // Clear rpmReduceField when statorInstalled is false (for both alternators)
    if (!formData.statorInstalled) {
      setValue('rpmReduceField', false)
    }
    if (!formData.statorInstalled2) {
      setValue('rpmReduceField2', false)
    }

    // Clear boostMode when rpmReduceField is false (nested condition)
    if (!formData.rpmReduceField) {
      setValue('boostMode', false)
    }
    if (!formData.rpmReduceField2) {
      setValue('boostMode2', false)
    }
  }, [formData.numAlternatorsInstalled, formData.statorInstalled, formData.statorInstalled2, formData.rpmReduceField, formData.rpmReduceField2, setValue])

  // Determine if this is an Altion (not Altion Max) device
  const isAltionDevice = useMemo(() => {
    if (!deviceInfo || !deviceInfo.deviceType) return false
    const deviceType = deviceInfo.deviceType.toLowerCase()
    return deviceType.includes('altion') && !deviceType.includes('max')
  }, [deviceInfo])

  // Helper function to compare hardware versions
  const isHardwareVersionAtLeast = useMemo(() => {
    return (targetVersion) => {
      if (!deviceInfo || !deviceInfo.regulatorHardwareVersion) return false
      return compareVersions(deviceInfo.regulatorHardwareVersion, targetVersion) >= 0
    }
  }, [deviceInfo])

  // Check if field polarity should be hidden (hardware version >= 1.1.0)
  const shouldHideFieldPolarity = useMemo(() => {
    return isHardwareVersionAtLeast('1.1.0')
  }, [isHardwareVersionAtLeast])

  // Set default alternator count for Altion devices (single alternator only)
  React.useEffect(() => {
    if (isAltionDevice && setValue) {
      // Always ensure Altion devices are set to single alternator
      if (!formData.numAlternatorsInstalled || formData.numAlternatorsInstalled !== 'numAlternatorsInstalled1') {
        setValue('numAlternatorsInstalled', 'numAlternatorsInstalled1')
      }
    }
  }, [isAltionDevice, setValue, formData])

  // Memoize field blur handler to prevent unnecessary re-renders
  const handleFieldBlur = useMemo(() =>
    (fieldName) => (e) => {
      if (onBlur) {
        onBlur(fieldName, e.target.value)
      }
    }, [onBlur],
  )

  // Memoized error rendering function
  const renderFieldError = useCallback((fieldName) => {
    const error = getFieldError ? getFieldError(fieldName) : errors[fieldName]
    return error && <div className="text-danger">{error.message}</div>
  }, [getFieldError, errors])

  // Memoized CSS class helper
  const getInputClassName = useCallback((fieldName) =>
    `form-control ${getFieldError?.(fieldName) ? 'is-invalid' : ''}`,
    [getFieldError],
  )

  // Helper function to render number input fields
  const renderNumberInput = useCallback((fieldName, label, options = {}) => {
    const { placeholder, helpText, ...inputProps } = options

    return (
      <div className="mb-3">
        <label htmlFor={fieldName} className="form-label">
          {label}
          {helpText && (
            <i
              className="bi bi-info-circle ms-1"
              data-bs-toggle="popover"
              data-bs-trigger="click"
              data-bs-content={helpText}
            />
          )}
        </label>
        <input
          type="number"
          className={getInputClassName(fieldName)}
          id={fieldName}
          placeholder={placeholder}
          {...getFieldValidationAttributes(fieldName, formData)}
          {...register(fieldName, getFieldRegistration(fieldName, formData))}
          onBlur={handleFieldBlur(fieldName)}
          {...inputProps}
        />
        {renderFieldError(fieldName)}
      </div>
    )
  }, [getInputClassName, formData, register, handleFieldBlur, renderFieldError])

  // Helper function to render radio button groups
  const renderRadioGroup = useCallback((fieldName, label, options, helpText) => (
    <div className="mb-3">
      <div className="form-label">
        {label}
        {helpText && (
          <i
            className="bi bi-info-circle ms-1"
            data-bs-toggle="popover"
            data-bs-trigger="click"
            data-bs-content={helpText}
          />
        )}
      </div>
      <div className="btn-group mb-3" role="group" aria-label={`${label} selection`}>
        {options.map(({ value, id, label: optionLabel }) => (
          <React.Fragment key={value}>
            <input
              type="radio"
              className="btn-check"
              value={value}
              id={id}
              {...register(fieldName, getFieldRegistration(fieldName, formData))}
            />
            <label className="btn btn-outline-primary" htmlFor={id}>
              {optionLabel}
            </label>
          </React.Fragment>
        ))}
      </div>
      {renderFieldError(fieldName)}
    </div>
  ), [register, formData, renderFieldError])

  // Helper function to render checkbox switches
  const renderCheckboxSwitch = useCallback((fieldName, label, helpText) => (
    <div className="form-check form-switch mb-3">
      <input
        className="form-check-input"
        type="checkbox"
        role="switch"
        id={fieldName}
        {...register(fieldName)}
      />
      <label className="form-check-label" htmlFor={fieldName}>
        {label}
      </label>
      {helpText && (
        <i
          className="bi bi-info-circle ms-1"
          data-bs-toggle="popover"
          data-bs-trigger="click"
          data-bs-content={helpText}
        />
      )}
    </div>
  ), [register])

  // Optimized RPM chart data change handler with useCallback
  const handleRpmDataChange = useCallback((chartId) => (newDataPoints) => {
    if (!setValue) return

    newDataPoints.forEach((value, index) => {
      const fieldName = generateRpmFieldName(RPM_FIELD_LETTERS[index], chartId || '')
      setValue(fieldName, value)
    })
  }, [setValue])

  // Memoized RPM field data computation for better performance
  const getRpmFieldData = useMemo(() =>
    (chartId = '') => RPM_FIELD_LETTERS.map(letter => {
      const fieldName = generateRpmFieldName(letter, chartId)
      return formData[fieldName] ?? 100 // Use nullish coalescing to properly handle 0
    }), [formData],
  )

  // Memoized constants for better performance and stable references
  const ALTERNATOR_LABELS = useMemo(() => ({ '': 'one', '2': 'two' }), [])
  const BATTERY_OPTIONS = useMemo(() => [
    { value: 'numBatteriesManaged1', id: 'numBatteriesManaged1', label: 'One' },
    { value: 'numBatteriesManaged2', id: 'numBatteriesManaged2', label: 'Two' },
  ], [])
  const POLARITY_OPTIONS = useCallback((suffix) => [
    { value: 'pType', id: `pType${suffix}`, label: 'P-type' },
    { value: 'nType', id: `nType${suffix}`, label: 'N-type' },
  ], [])

  // Memoized alternator fields renderer for better performance
  const renderAlternatorFields = useCallback((suffix = '') => {
    const isAlt2 = suffix === '2'
    const altNumber = ALTERNATOR_LABELS[suffix]

    return (
      <div className="mb-4">
        {/* Alternator Sub-Card */}
        <div
          className="card border-0 mb-3"
          style={{
            background: isAlt2 ? 'linear-gradient(to right, #fff3cd, #fdf6e3)' : 'linear-gradient(to right, #e8f5e8, #f0f8f0)',
            border: `2px solid ${isAlt2 ? '#ffc107' : '#28a745'} !important`,
          }}
        >
          <div className="card-header fw-bold fs-6" style={{
            background: 'transparent',
            borderBottom: `1px solid ${isAlt2 ? '#ffc107' : '#28a745'}`,
          }}>
            <i className={`bi bi-lightning-charge-fill ${isAlt2 ? 'text-warning' : 'text-success'} fs-5 me-2`}></i>
            Alternator {altNumber}
          </div>

          <div className="card-body">
            {/* Battery bank selection - only visible for Alternator Two */}
            {isAlt2 && (
              <>
                <div id="batBanksNumDiv2">
                  {renderRadioGroup(
                    'numBatteriesManaged',
                    'Battery bank connected to alternator',
                    BATTERY_OPTIONS,
                  )}
                </div>

                {/* Hidden input to generate the numBatteriesManaged2 field for regulator.js compatibility */}
                {formData.numBatteriesManaged === 'numBatteriesManaged2' && (
                  <input
                    type="hidden"
                    name="numBatteriesManaged2"
                    value="numBatteriesManaged2"
                    {...register('numBatteriesManaged2')}
                  />
                )}

                {/* Ignition Required checkbox - only for Alternator 2 and hardware >= 1.1.0 */}
                {isHardwareVersionAtLeast('1.1.0') && renderCheckboxSwitch(
                  'ignitionRequired2',
                  'Require ignition to be on for charging',
                  'When enabled, this alternator will only charge when the ignition is on. Alternator 1 always requires its ignition to be on.',
                )}
              </>
            )}

            {!isAlt2 && (
              <div className="alert alert-info border-0 mb-3" style={{ background: 'rgba(13, 202, 240, 0.1)' }}>
                <i className="bi bi-info-circle-fill me-2"></i>
                <strong>This should connect to your primary i.e., house battery bank</strong>
              </div>
            )}

            {renderNumberInput(
              `fieldVoltage${suffix}`,
              'Maximum field voltage (%)',
              {
                helpText: 'Typically 100%. Many 48V alternators use 12V field. In these cases set at ~25% to derate your alternator. This setting scales the Minimum field drive (%) and RPM power reduction values to prevent exceeding the alternator\'s field power capability.',
              },
            )}

            {/* Field polarity - only show for hardware versions < 1.1.0 */}
            {!shouldHideFieldPolarity && renderRadioGroup(
              `fieldPolarity${suffix}`,
              'Field polarity',
              POLARITY_OPTIONS(suffix),
              'Most modern alternators are P-type',
            )}

            {renderNumberInput(
              `targetTemp${suffix}`,
              'Target temperature (°C)',
            )}

            {renderNumberInput(
              `startupDelay${suffix}`,
              'Startup delay (seconds)',
              {
                helpText: 'Leave blank to disable setting',
              },
            )}

            {renderCheckboxSwitch(
              `statorInstalled${suffix}`,
              'Stator sensor installed',
            )}

            {/* Stator Configuration Section */}
            {formData[`statorInstalled${suffix}`] && (
              <div id={`statorDiv${suffix}`}>
                {renderNumberInput(
                  `pulleyRatio${suffix}`,
                  'Engine : Alternator pulley diameter ratio',
                  {
                    helpText: 'e.g., if engine pulley is 6 inches in diameter and alternator pulley is 3 in diameter, the ratio is 2',
                  },
                )}

                {renderNumberInput(
                  `altPoles${suffix}`,
                  'Number of alternator poles',
                )}

                {renderNumberInput(
                  `minField${suffix}`,
                  'Minimum field drive (%)',
                  {
                    helpText: 'Minimum field drive to maintain tachometer RPM signal. 6% is typical. Set at the minimum possible level to prevent unintentional charging while retaining RPM readings. This value is automatically scaled by the Maximum field voltage (%) setting.',
                  },
                )}            {renderCheckboxSwitch(
                  `rpmReduceField${suffix}`,
                  'Reduce power draw at specified RPM',
                )}

                {/* RPM Graph Section - Conditionally shown when rpmReduceField is checked */}
                {formData[`rpmReduceField${suffix}`] && (
                  <div id={`statorGraphDiv${suffix}`}>
                    {renderNumberInput(
                      `maxRpm${suffix}`,
                      'Engine maximum RPM',
                    )}

                    <div>
                      Maximum alternator power by engine RPM
                      <i
                        className="bi bi-info-circle ms-1"
                        data-bs-toggle="popover"
                        data-bs-trigger="click"
                        data-bs-content="Reduces maximum alternator power at different RPM ranges. Tap maximum power level points to set the RPM limits desired. These values are automatically scaled by the Maximum field voltage (%) setting to prevent exceeding the alternator's field power capability."
                      ></i>
                    </div>
                    <div className="mb-3">
                      <RpmFieldChart
                        chartId={suffix}
                        maxRpm={formData[`maxRpm${suffix}`] || 3000}
                        rpmFieldData={getRpmFieldData(suffix)}
                        onDataChange={handleRpmDataChange(suffix)}
                        disabled={false}
                      />
                    </div>

                    {renderCheckboxSwitch(
                      `boostMode${suffix}`,
                      'Boost mode',
                      'Intelligently boosts charging power whenever engine speed is stable, maximizing alternator output across all RPM ranges. Automatically adjusts back to normal operation when RPM fluctuates, ensuring optimal balance of propulsion and charging.',
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Alternator Current Shunt Section */}
            <div id={`altShuntUltimateParentDiv${suffix}`}>
              {renderCheckboxSwitch(
                `altShuntInstalled${suffix}`,
                'Alternator current shunt installed',
                'Enable this to use Battery 2\'s shunt to measure Alternator 1 current, and if applicable, Battery 1\'s shunt to measure Alternator 2 current. This requires a CAN bus connection to measure total battery current if you have two alternators. Alternator current measurement is required for Victron DVCC.',
              )}

              {formData[`altShuntInstalled${suffix}`] && (
                <div id={`altShuntDiv${suffix}`}>
                  {renderNumberInput(
                    `altShuntAmps${suffix}`,
                    'Shunt current rating (amps)',
                  )}

                  {renderNumberInput(
                    `altShuntMv${suffix}`,
                    'Shunt voltage rating (mV)',
                  )}

                  {renderNumberInput(
                    `altCurrentLimit${suffix}`,
                    'Alternator current limit (amps)',
                    {
                      helpText: 'Leave blank to disable setting.',
                    },
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }, [formData, renderNumberInput, renderRadioGroup, renderCheckboxSwitch, register, handleRpmDataChange, getRpmFieldData, BATTERY_OPTIONS, ALTERNATOR_LABELS, POLARITY_OPTIONS, shouldHideFieldPolarity, isHardwareVersionAtLeast])

  // Main alternator configuration options
  const ALTERNATOR_OPTIONS = useMemo(() => [
    { value: 'numAlternatorsInstalled1', id: 'numAlternatorsInstalled1', label: 'One' },
    { value: 'numAlternatorsInstalled2', id: 'numAlternatorsInstalled2', label: 'Two' },
  ], [])

  return (
    <div
      className="card shadow-sm border-0 mb-4"
      style={{
        background: 'linear-gradient(to right, #ffffff, #f8f9fa)',
        transition: 'all 0.3s ease',
      }}
    >
      {/* Card Header - Modern Design */}
      <div className="card-header fw-bold border-0 fs-5">
        <i className="bi bi-lightning-charge-fill text-warning fs-3 me-2" aria-hidden="true"></i>
        <span>Alternators</span>
      </div>

      {/* Card Body */}
      <div className="card-body">
        {/* Show loading state while device info is being fetched */}
        {!deviceInfo ? (
          <div className="d-flex align-items-center justify-content-center py-3">
            <div className="spinner-border spinner-border-sm me-2" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <span className="text-muted">Loading device information...</span>
          </div>
        ) : (
          <>
            {/* Number of Alternators Selection - Hidden for Altion devices */}
            {!isAltionDevice && (
              <div className="mb-4">
                {renderRadioGroup(
                  'numAlternatorsInstalled',
                  'Alternators regulated',
                  ALTERNATOR_OPTIONS,
                )}
              </div>
            )}

            {/* Alternator One */}
            {renderAlternatorFields('')}

            {/* Alternator Two (if selected and not Altion device) */}
            {!isAltionDevice && formData.numAlternatorsInstalled === 'numAlternatorsInstalled2' && renderAlternatorFields('2')}
          </>
        )}
      </div>
    </div>
  )
}

export default AlternatorSection
